import { createEl } from "./utils.js";

export default class TimeInput {
    #root;
    #comboBox;
    #listBox;
    defaultValue;
    #onBlur;
    #minTime;
    #el;

    constructor({defaultValue = TimeInput.recommendedTime, onBlur = null, minTime = null }) {
        const [ el ] = createEl();
        this.#el = el;
        this.#root = this.#el("div", { class: "time-input" });
        this.defaultValue = defaultValue;
        this.#onBlur = onBlur;
        this.#minTime = minTime;

        this.#createComboBox();
        this.#createListBox();
        this.#createOptions();

        this.#comboBox.addEventListener("click", (event) => {
            event.preventDefault();
            this.#showListBox(event);
        });

        document.addEventListener("click", (event) => {
            if(!this.#comboBox.contains(event.target)) {
                this.#hideListBox();
            }
        });
    }

    static pad(n) {
        const nStr = n.toString();
        return nStr.length < 2 ? `0${nStr}` : nStr;
    }


    static recommendedTime() {
        const current = new Date();
        let defaultHours = current.getHours();
        let defaultMinutes = 30;

        if(current.getMinutes() >= 30) {
            defaultHours += 1;
            defaultMinutes = 0;
        }

        if(defaultHours == 24) {
            defaultHours = 0;
        }


        return `${TimeInput.pad(defaultHours)}:${TimeInput.pad(defaultMinutes)}`;
    }

    static nextHour(time) {
        const values = time.split(":");
        const date = new Date();
        const hour = parseInt(values[0], 10);
        const minutes = parseInt(values[1], 10);
        date.setHours(hour + 1 > 23 ? hour : hour + 1);
        date.setMinutes(minutes);
        return `${TimeInput.pad(date.getHours())}:${TimeInput.pad(date.getMinutes())}`;
    }

    get root() {
        return this.#root;
    }

    get value() {
        return this.#comboBox.value;
    }

    set value(newValue) {
        this.#comboBox.value = newValue;
        this.#validate();
    }

    set minTime(value) {
        this.#minTime = value;
        this.#createOptions();
    }

    get minTime() {
        return this.#minTime;
    }

    #showListBox() {
        if(this.#listBox.classList.contains("hidden")) {
            this.#listBox.classList.remove("hidden");
            this.#onComboBoxInput();
        }
    }

    #hideListBox() {
        this.#listBox.classList.add("hidden");
    }

    #validate() {
        const value = this.#comboBox.value.trim();
        let tokens = value.split(":");
        let hours = NaN;
        let minutes = NaN;

        if(tokens.length === 2) {
            hours = parseInt(tokens[0], 10);
            minutes = parseInt(tokens[1], 10);
        } else if(tokens.length === 1) {
            hours = parseInt(tokens[0].substring(0, 2), 10);
            minutes = parseInt(tokens[0].substring(2, 4), 10);
        }

        if(Number.isNaN(hours) || Number.isNaN(minutes)) {
            this.#comboBox.value = this.defaultValue();
        } else {
            if(hours < 0 || hours > 23) {
                this.#comboBox.value = this.defaultValue();
            } else if(minutes < 0 || minutes > 60) {
                this.#comboBox.value = this.defaultValue();
            } else {
                this.#comboBox.value = `${TimeInput.pad(hours)}:${TimeInput.pad(minutes)}`;
            }
        }
    }

    #createComboBox() {
        this.#comboBox = this.#el("input", {
            type: "text",
            autocapitalize: "none",
            autocomplete: "off",
            role: "combobox",
            value: this.defaultValue(),
        })

        this.#comboBox.addEventListener("keyup", this.#onComboBoxKeyUp.bind(this));
        this.#comboBox.addEventListener("keydown", this.#onComboBoxKeyDown.bind(this));
        this.#comboBox.addEventListener("focus", this.#showListBox.bind(this));
        this.#comboBox.addEventListener("blur", this.#onComboBoxBlur.bind(this));

        this.#root.appendChild(this.#comboBox);
    }

    #createListBox() {
        this.#listBox = this.#el("ul", {
            role: "listbox",
            class: "hidden",
        });

        this.#listBox.addEventListener("mouseover", this.#onListBoxMouseOver.bind(this));
        this.#listBox.addEventListener("click", this.#onListBoxClick.bind(this));
        this.#listBox.addEventListener("keydown", this.#onListBoxKeyDown.bind(this));

        this.#root.appendChild(this.#listBox);
    }

    #createOptions() {
        this.#listBox.innerHTML = "";
        for (let i = 0; i <= 23; i++) {
            for (let j = 0; j < 60; j+=15) {
                const li = this.#el("li", { tabindex: "-1" }, `${TimeInput.pad(i)}:${TimeInput.pad(j)}`);
                if(typeof this.#minTime === "string" && this.#minTime.localeCompare(li.textContent) > 0) {
                    continue;
                }
                this.#listBox.appendChild(li);
            }
        }
    }

    #onComboBoxKeyUp(event) {
        const options = this.#listBox.querySelectorAll("li");
        switch(event.key) {
            case "ArrowUp":
            case "ArrowLeft":
            case "ArrowRight":
            case " ":
            case "Enter":
            case "Tab":
            case "Shift":
                break;
            case "ArrowDown":
                this.#showListBox();
                for(let i = 0; i < options.length; i++) {
                    if(options[i].classList.contains("highlight")) {
                        options[i].focus();
                        this.#comboBox.value = options[i].textContent;
                        break;
                    }
                }
                break;
            case "Escape":
                this.#hideListBox();
                break;
            default:
                this.#onComboBoxInput();
        }
    }

    #onComboBoxKeyDown(event) {
        switch(event.key) {
            case "Enter":
                event.preventDefault();
            case "Tab":
                this.#hideListBox();
                break;
        }
    }

    #onComboBoxInput() {
        const currentInput = this.#comboBox.value.trim();
        const options = this.#listBox.querySelectorAll("li");
        let toHighlight = null;

        for(let i = 0; i < options.length; i++) {
            if(options[i].classList.contains("highlight")) {
                options[i].classList.remove("highlight");
            }
        }

        for(let i = 0; i < options.length; i++) {
            if(options[i].textContent.trim() === currentInput) {
                toHighlight = options[i];
                break;
            }
        }

        if(toHighlight === null) {
            for(let i = 0; i < options.length; i++) {
                const content = options[i].textContent.trim();
                if(content.startsWith(currentInput) || content.replaceAll(":", "").startsWith(currentInput)) {
                    toHighlight = options[i];
                    break;
                }
            }
        }

        if(toHighlight !== null) {
            toHighlight.scrollIntoView({
                block: "center",
                container: "nearest",
            });
            toHighlight.classList.add("highlight");
        }
    }

    #onComboBoxBlur(event) {
        event.preventDefault();
        this.#validate();
        this.#onBlur();
    }

    #onListBoxMouseOver(event) {
        event.preventDefault();
        if(event.target.tagName === "LI") {
            const options = this.#listBox.querySelectorAll("li");
            for(let i = 0; i < options.length; i++) {
                if(options[i].classList.contains("highlight")) {
                    options[i].classList.remove("highlight");
                }
            }
            event.target.classList.add("highlight");
        }
    }

    #onListBoxClick(event) {
        event.preventDefault();
        if(event.target.tagName === "LI") {
            this.#comboBox.value = event.target.textContent;
            this.#comboBox.focus();
            this.#hideListBox();
        }
    }

    #onListBoxKeyDown(event) {
        event.preventDefault();
        const options = this.#listBox.querySelectorAll("li");
        switch(event.key) {
            case "ArrowUp":
                for(let i = 0; i < options.length; i++) {
                    if(options[i].classList.contains("highlight")) {
                        if(options[i].previousElementSibling) {
                            options[i].classList.remove("highlight");
                            options[i].previousElementSibling.classList.add("highlight");
                            options[i].previousElementSibling.focus();
                            this.#comboBox.value = options[i].textContent;
                            break;
                        }
                    }
                }
                break;
            case "ArrowDown":
                for(let i = 0; i < options.length; i++) {
                    if(options[i].classList.contains("highlight")) {
                        if(options[i].nextElementSibling) {
                            options[i].classList.remove("highlight");
                            options[i].nextElementSibling.classList.add("highlight");
                            options[i].nextElementSibling.focus();
                            this.#comboBox.value = options[i].textContent;
                            break;
                        }
                    }
                }
                break;
            case "Enter":
                for(let i = 0; i < options.length; i++) {
                    if(options[i].classList.contains("highlight")) {
                        this.#comboBox.value = options[i].textContent;
                        this.#hideListBox();
                        this.#comboBox.focus();
                        break;
                    }
                }
                break;
            case "Escape":
                this.#comboBox.focus();
                this.#hideListBox();
                break;
            case "Tab":
                this.#hideListBox();
                break;
            default:
                this.#comboBox.focus();
        }
    }
}
